// Pure decision/formatting helpers for the evidence auto-draft (PROD-7808 E1).
// No I/O here — details.jsx POSTs the draft endpoint and renders these results
// (unit-tested in evidence_draft_helpers.test.js). Shapes follow the E1 frozen contract.

// classify_draft_response: bucket the HTTP-200 draft payload for rendering.
// 'error' = missing/empty envelope; 'ineligible' = no field drafts, reasons explain.
// 'draft' requires a fields object — eligible:true without fields would crash the render.
export const classify_draft_response = (data) => {
  const d = data || {};
  if (d.eligible === false) {
    return { kind: 'ineligible', reasons: Array.isArray(d.reasons) ? d.reasons : [] };
  }
  if (d.fields && typeof d.fields === 'object') return { kind: 'draft', reasons: [] };
  return { kind: 'error', reasons: [] };
};

// classify_field: render state for one drafted field {value, verdict, source, note}.
// harms → value NEVER prefilled (R3: the strategy call stays visibly human);
// source=failed → DEGRADED, source=fallback_signup → account-not-cardholder address —
// neither is prefilled: an unverified value must not be one-shot-submittable as if
// verified (P2/SF2/F2); the operator types it deliberately.
export const classify_field = (field) => {
  const f = field || {};
  const verdict = String(f.verdict || '').toLowerCase();
  const source = String(f.source || '').toLowerCase();
  const harms = verdict === 'harms';
  const degraded = source === 'failed';
  const fallback = source === 'fallback_signup';
  return {
    prefill: harms || degraded || fallback ? '' : f.value || '',
    harms,
    degraded,
    fallback,
    note: f.note || ''
  };
};

export const harms_warning_text = (note) =>
  `This would argue the customer's case: ${note || 'facts favor the customer'}. Draft withheld — write manually only if you have a counter.`;

// apply_draft_to_evidence: merge drafted values into the form state. Fields absent
// from the draft keep their current value; harms fields are cleared (never submitted silently).
export const apply_draft_to_evidence = (evidence, draft, text_defs) => {
  const next = { ...(evidence || {}) };
  const fields = (draft && draft.fields) || {};
  (text_defs || []).forEach(([key]) => {
    if (!fields[key]) return;
    const cls = classify_field(fields[key]);
    next[key] = cls.harms ? '' : cls.prefill;
  });
  return next;
};

// recommendation_banner: consider_accept is the loud warning; contest is a calm confirmation.
export const recommendation_banner = (draft) => {
  const d = draft || {};
  if (!d.recommendation) return null;
  if (d.recommendation === 'consider_accept') {
    return {
      tone: 'warning',
      text: `Recommend Accept, not contest: ${d.recommendation_reason || 'see draft notes'}`
    };
  }
  return {
    tone: 'info',
    text: `Recommendation: contest${d.recommendation_reason ? ` — ${d.recommendation_reason}` : ''}`
  };
};

export const draft_age_text = (generated_at, now_ms = Date.now()) => {
  if (!generated_at) return '';
  const t = Date.parse(generated_at);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.floor((now_ms - t) / 60000));
  if (mins < 1) return 'drafted just now';
  if (mins < 60) return `drafted ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `drafted ${hours}h ago`;
  return `drafted ${Math.floor(hours / 24)}d ago`;
};

// submit_confirm_summary: enumerate every empty / degraded / fallback / harms field
// before the one-shot Stripe submit spends itself with an invisible hole (P2/R5).
// One line per field with an issue; empty array = nothing notable.
export const submit_confirm_summary = (evidence, draft, text_defs, file_defs) => {
  const ev = evidence || {};
  const fields = (draft && draft.fields) || {};
  const lines = [];
  // No successful draft → say so explicitly; "no warnings" must not read as "all clear" (SF5).
  if (classify_draft_response(draft).kind !== 'draft') {
    lines.push(
      'No draft was generated for this dispute — recommendation and harms analysis unavailable'
    );
  }
  // Accept recommendation belongs in the same list the operator reads pre-submit (F4).
  if ((draft || {}).recommendation === 'consider_accept') {
    lines.push(
      `Recommendation is ACCEPT, not contest: ${
        draft.recommendation_reason || 'see draft notes'
      } — reconsider before submitting`
    );
  }
  (text_defs || []).forEach(([key, label]) => {
    const cls = classify_field(fields[key]);
    const filled = Boolean(String(ev[key] || '').trim());
    if (!filled && cls.harms) {
      lines.push(`${label}: omitted (draft withheld — facts argue the customer's case)`);
    } else if (!filled && cls.degraded) {
      lines.push(`${label}: empty (draft fetch failed) — will be omitted; verify manually`);
    } else if (!filled && cls.fallback) {
      lines.push(
        `${label}: empty (only the account signup address was available, not prefilled) — will be omitted`
      );
    } else if (!filled) {
      lines.push(`${label}: empty — will be omitted`);
    } else if (cls.harms) {
      lines.push(`${label}: filled manually despite a harms flag — review before submitting`);
    } else if (cls.degraded) {
      lines.push(`${label}: draft fetch failed — verify the value manually`);
    } else if (cls.fallback) {
      lines.push(`${label}: account address from signup, not the cardholder's statement address`);
    }
  });
  (file_defs || []).forEach(([key, label]) => {
    if (!ev[key]) lines.push(`${label}: no file uploaded`);
  });
  return lines;
};
